import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage as amplifySessionStorage } from "aws-amplify/utils";

import { webCommonHost } from "./host";
import { runtimeEnv } from "./runtime-env";

/**
 * The group this surface admits, and the one it refuses. Mirrored per surface
 * (customer/admin on web-customer, admin/customer on web-admin), so the host
 * supplies them. Functions, not constants: the host is set at boot and these
 * are only ever read inside a call.
 */
export const requiredGroup = () => webCommonHost().requiredGroup;
export const oppositeGroup = () => webCommonHost().oppositeGroup;

export interface CognitoEnv {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
}

function readEnv(): CognitoEnv {
  const region = runtimeEnv("VITE_COGNITO_REGION");
  const userPoolId = runtimeEnv("VITE_COGNITO_USER_POOL_ID");
  const userPoolClientId = runtimeEnv("VITE_COGNITO_CLIENT_ID");
  if (!region || !userPoolId || !userPoolClientId) {
    throw new Error(
      "Cognito config missing — set VITE_COGNITO_REGION, VITE_COGNITO_USER_POOL_ID, and VITE_COGNITO_CLIENT_ID for this deployment.",
    );
  }
  return {
    region,
    userPoolId,
    userPoolClientId,
  };
}

let configured = false;

/**
 * Configure Amplify v6 once on app boot. Idempotent so HMR doesn't blow up.
 * Pool config is the SHARED Cognito user pool; each SPA passes its own
 * client id (web-customer → web client, web-admin → admin client) per
 * ADR-0010 §3.3.
 */
export function configureCognito(): void {
  if (configured) return;
  const env = readEnv();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.userPoolId,
        userPoolClientId: env.userPoolClientId,
        // Pool's username attribute is `email` (per BACKEND-HANDOFF.md /
        // brief §1). Amplify needs to know this so signUp/signIn pass the
        // email through the right slot.
        loginWith: { email: true },
      },
    },
  });
  // Cognito tokens stay in sessionStorage (not Amplify's localStorage default).
  //
  // Originally this mirrored the 2026-06-20 "session dies on browser close"
  // rule. That rule is superseded (ADR-0036) — the Penn session now lives in the
  // `penn_rt` cookie — but the override is DELIBERATELY kept: since the Penn
  // cookie carries the session, these tokens are load-bearing only for the
  // first exchange and the fallback re-exchange, and keeping them per-tab
  // leaves the smaller blast radius. Do not "fix" this to match the old
  // comment's reasoning.
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
  configured = true;
}
